/**
 * ## ⛔ The boundary panel
 *
 * The product tells institutions that no child's data has ever left the system
 * for a model. Three guards in the backend enforce it. This screen is what
 * makes the claim **observable** rather than asserted — and it is the only
 * thing on the page that matters this much, so it is one sentence, at the top,
 * in a colour that means something.
 *
 * There is no "probably fine" state. Either the count is zero or it is not.
 */

export type ViolationReason = "guardFired" | "unknownKind" | "oversizedField";

export interface BoundaryViolation {
  id: string;
  institutionId: string;
  feature: string;
  kind?: string;
  reason: ViolationReason;
  at: string;
  usageId?: string;
  source: "guard" | "audit";
}

export interface AuditResult {
  at: string;
  checked: number;
  found: number;
  total: number;
}

export interface BoundaryReport {
  total: number;
  recent: BoundaryViolation[];
  lastAudit: AuditResult | undefined;
}

/**
 * Three states, and the third is not "fine".
 *
 * `unaudited` is its own answer because "0 violations" from a monitor that has
 * never run is not evidence of anything — and a green panel above a check
 * nobody performed is worse than no panel.
 */
export type BoundaryVerdict = "clean" | "violated" | "unaudited";

export function verdictOf(report: BoundaryReport | undefined): BoundaryVerdict {
  if (!report) return "unaudited";
  if (report.total > 0) return "violated";
  /*
    An audit that read nothing has proved nothing.

    On 2026-09-09 this page said "0 boundary violations, all time", green,
    under a line reading "across 0 calls" — while twenty-three calls were
    listed below it. The audit had run at half past eight, when the log was
    empty, and every call came after. Green over an empty read is the worst
    sentence this page could print, so it is amber and says so.
  */
  if (!report.lastAudit || report.lastAudit.checked === 0) return "unaudited";
  return "clean";
}

export const VERDICT_KEY: Record<BoundaryVerdict, string> = {
  clean: "ai.boundary.clean",
  violated: "ai.boundary.violated",
  unaudited: "ai.boundary.unaudited",
};

/** Green, red, and the amber that means nobody has looked. */
export const VERDICT_TONE: Record<BoundaryVerdict, string> = {
  clean: "border-cue-green bg-cue-green-soft text-ink",
  violated: "border-accent bg-accent-soft text-ink",
  unaudited: "border-cue-amber bg-cue-amber-soft text-ink",
};

export const REASON_KEY: Record<ViolationReason, string> = {
  guardFired: "ai.boundary.reason.guardFired",
  unknownKind: "ai.boundary.reason.unknownKind",
  oversizedField: "ai.boundary.reason.oversizedField",
};

/**
 * How stale the last audit is, in whole days.
 *
 * The job runs daily. Anything older than two days means the job is not
 * running, which is a different problem from a violation and needs saying
 * separately.
 */
export const STALE_AFTER_DAYS = 2;

export function auditAgeDays(report: BoundaryReport | undefined, now: number): number | undefined {
  const at = report?.lastAudit?.at;
  if (!at) return undefined;

  const ms = Date.parse(at);
  if (!Number.isFinite(ms)) return undefined;
  return Math.floor((now - ms) / 86_400_000);
}

export function auditIsStale(report: BoundaryReport | undefined, now: number): boolean {
  const age = auditAgeDays(report, now);
  return age !== undefined && age > STALE_AFTER_DAYS;
}
